import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, CalendarClock, FolderCheck, ShieldCheck, ArrowRight, Check,
  Menu, X, FileText, BarChart3, Wallet, Fingerprint,
} from "lucide-react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import BookDemoDialog from "../components/BookDemoDialog";

const pillars = [
  { icon: Users, title: "People Management", desc: "Central employee profiles, org structure and self-service — every record in one secure place." },
  { icon: CalendarClock, title: "Leave & Attendance", desc: "Track leave balances, public holidays and working hours in line with Botswana law." },
  { icon: FolderCheck, title: "Documents & Records", desc: "Store contracts and statutory records with org-scoped, audit-ready file storage." },
  { icon: ShieldCheck, title: "Compliance Made Easy", desc: "Stay aligned with the Employment & Labour Relations Act, 2025 — without the paperwork." },
];

const features = [
  { icon: Users, title: "Employee spine", desc: "Fields aligned to Schedule 4 records: identity, next-of-kin, wages, hours, leave, termination." },
  { icon: Wallet, title: "Payroll-ready", desc: "Wage components, pay intervals and night-work allowance captured from day one." },
  { icon: FileText, title: "Statutory records", desc: "Keep the records every Botswana employer must maintain under section 210." },
  { icon: BarChart3, title: "Insightful dashboards", desc: "Headcount, leave trends and compliance rate at a glance." },
  { icon: Fingerprint, title: "Multi-tenant & secure", desc: "Strict organisation isolation — your data never mixes with another business." },
  { icon: ShieldCheck, title: "Audit trail", desc: "Append-only log of every create, update and delete for compliance evidence." },
];

const plans = [
  { name: "Starter", price: "P0", period: "/mo", tagline: "For very small teams getting started", features: ["Up to 10 employees", "Employee records", "Leave tracking", "Email support"], cta: "Start free" },
  { name: "Growth", price: "P450", period: "/mo", tagline: "For growing SMEs that need compliance", features: ["Up to 100 employees", "Documents & records", "Compliance dashboard", "Payroll-ready exports", "Priority support"], cta: "Start free trial", featured: true },
  { name: "Enterprise", price: "Custom", period: "", tagline: "For larger organisations", features: ["Unlimited employees", "Advanced reporting", "Custom domain", "Dedicated onboarding", "SLA & training"], cta: "Contact sales" },
];

function NavBar({ onDemo }) {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "Features", href: "#features" },
    { label: "Solutions", href: "#solutions" },
    { label: "Pricing", href: "#pricing" },
    { label: "Compliance", href: "#compliance" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <Logo size="sm" showTagline={false} />
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.label} href={l.href} className="text-sm font-medium text-slate-600 hover:text-brand-blue transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-brand-blue transition-colors">Sign in</Link>
          <Button onClick={onDemo} className="bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-10 px-5">Book a Demo</Button>
        </div>
        <button className="md:hidden text-slate-700" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white px-5 py-4 space-y-3">
          {links.map((l) => (
            <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="block text-sm font-medium text-slate-700">{l.label}</a>
          ))}
          <div className="flex gap-3 pt-2">
            <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-lg">Sign in</Button></Link>
            <Button onClick={onDemo} className="flex-1 bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg">Book a Demo</Button>
          </div>
        </div>
      )}
    </header>
  );
}

function DashboardPreview() {
  return (
    <div className="relative rounded-2xl bg-white shadow-2xl shadow-brand-blue/10 ring-1 ring-slate-100 p-4 sm:p-5 animate-floaty">
      <div className="flex items-center justify-between mb-4">
        <Logo size="sm" showTagline={false} />
        <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-200" /><span className="w-2.5 h-2.5 rounded-full bg-slate-200" /><span className="w-2.5 h-2.5 rounded-full bg-brand-sky" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[{ l: "Employees", v: "128" }, { l: "On Leave", v: "15" }, { l: "Compliance", v: "98%" }].map((s) => (
          <div key={s.l} className="rounded-xl bg-brand-mist p-3">
            <div className="text-[11px] text-slate-500">{s.l}</div>
            <div className="text-xl font-bold text-brand-navy font-display">{s.v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-brand-mist p-4">
        <div className="text-xs font-semibold text-slate-500 mb-3">Leave Overview</div>
        <div className="flex items-end gap-1.5 h-24">
          {[35, 60, 48, 72, 55, 84, 66, 92, 78, 100].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-brand-sky to-brand-blue" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => { document.title = "HRFlo — People. Records. Compliance."; }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <NavBar onDemo={() => setDemoOpen(true)} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-light/60 to-white" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-sky/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-light text-brand-blue text-xs font-semibold px-3 py-1.5 mb-6">
              <ShieldCheck className="w-3.5 h-3.5" /> Built for the Employment & Labour Relations Act, 2025
            </span>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] text-brand-navy">
              People. Records.<br />Compliance.<br /><span className="text-brand-blue">All in Flow.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-md">
              The modern HR platform helping small and medium businesses in Botswana manage people and stay compliant — effortlessly.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup"><Button className="bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-12 px-7 text-base">Get started free <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Button onClick={() => setDemoOpen(true)} variant="outline" className="rounded-lg h-12 px-7 text-base border-slate-300 hover:border-brand-blue hover:text-brand-blue">Book a Demo</Button>
            </div>
            <p className="mt-4 text-sm text-slate-400">No credit card required · Set up in minutes</p>
          </div>
          <div className="animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <DashboardPreview />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-navy">Everything your HR needs, in one flow</h2>
          <p className="mt-4 text-slate-600">From onboarding to compliance evidence — HRFlo keeps every record accurate, secure and audit-ready.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p) => (
            <div key={p.title} className="group rounded-2xl border border-slate-100 bg-white p-6 hover:shadow-xl hover:shadow-brand-blue/5 hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center text-brand-blue mb-5 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                <p.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-lg text-brand-navy mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="bg-brand-mist py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1573164574397-dd250bc8a598?w=500&h=600&fit=crop" alt="Team collaborating" className="rounded-2xl object-cover h-full w-full shadow-lg" />
            <img src="https://images.unsplash.com/photo-1680309915319-d46a0e40a153?w=500&h=600&fit=crop" alt="Working on laptop" className="rounded-2xl object-cover h-full w-full shadow-lg mt-8" />
          </div>
          <div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-navy">Made for Botswana SMEs</h2>
            <p className="mt-4 text-slate-600">Whether you run a retail shop, a warehouse or a professional firm, HRFlo adapts to how you work — and keeps you on the right side of the law.</p>
            <div className="mt-8 grid gap-6">
              {features.slice(0, 4).map((f) => (
                <div key={f.title} className="flex gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center text-brand-blue"><f.icon className="w-5 h-5" /></div>
                  <div>
                    <h4 className="font-semibold text-brand-navy">{f.title}</h4>
                    <p className="text-sm text-slate-600">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section id="compliance" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 mb-5"><ShieldCheck className="w-3.5 h-3.5" /> Compliance built-in</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-navy">Aligned with the Act & Regulations</h2>
          <p className="mt-4 text-slate-600">HRFlo's records spine follows Schedule 4 (reg 22/23) of the Employment and Labour Relations Regulations, 2026 — so you keep exactly the records every employer must maintain under section 210.</p>
          <ul className="mt-6 space-y-3">
            {["Employee identity, next-of-kin & employment nature", "Wage rate, pay interval & wage components", "Leave, sick leave, public holidays & rest periods", "Commencement, termination dates & terminal payments", "Night-work allowance at 5% of basic salary (reg 24)"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700"><Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" /><span>{t}</span></li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-brand-navy to-brand-blue p-8 text-white shadow-xl">
          <div className="text-5xl font-display font-extrabold">98%</div>
          <div className="text-brand-skysoft mt-1">Average compliance rate across HRFlo orgs</div>
          <div className="mt-8 grid grid-cols-2 gap-6">
            {[{ v: "Schedule 4", l: "Records spine" }, { v: "Section 210", l: "Duty to keep records" }, { v: "Audit-ready", l: "Append-only log" }, { v: "Multi-tenant", l: "Full data isolation" }].map((s) => (
              <div key={s.l}><div className="font-display font-bold text-lg">{s.v}</div><div className="text-sm text-brand-skysoft">{s.l}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-brand-mist py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-navy">Simple, transparent pricing</h2>
            <p className="mt-4 text-slate-600">Placeholder tiers — start free and upgrade as your team grows.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => (
              <div key={p.name} className={`rounded-2xl p-7 bg-white border ${p.featured ? "border-brand-blue shadow-xl shadow-brand-blue/10 md:-translate-y-3" : "border-slate-100"}`}>
                {p.featured && <span className="inline-block text-xs font-semibold bg-brand-blue text-white px-3 py-1 rounded-full mb-3">Most popular</span>}
                <h3 className="font-display font-bold text-xl text-brand-navy">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1 h-10">{p.tagline}</p>
                <div className="mt-4 mb-6"><span className="font-display font-extrabold text-4xl text-brand-navy">{p.price}</span><span className="text-slate-500">{p.period}</span></div>
                <ul className="space-y-3 mb-7">
                  {p.features.map((f) => (<li key={f} className="flex items-center gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 shrink-0" />{f}</li>))}
                </ul>
                <Link to="/signup"><Button className={`w-full rounded-lg h-11 ${p.featured ? "bg-brand-blue hover:bg-brand-bluedark text-white" : "bg-white border border-slate-300 text-brand-navy hover:border-brand-blue hover:text-brand-blue"}`}>{p.cta}</Button></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="rounded-3xl bg-gradient-to-br from-brand-navy to-brand-blue px-8 sm:px-16 py-14 text-center text-white relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-2xl" />
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl relative">Your HR. In Flow.</h2>
          <p className="mt-4 text-brand-skysoft max-w-xl mx-auto relative">Automate processes, stay compliant and empower your people. Join Botswana SMEs building better workplaces with HRFlo.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center relative">
            <Link to="/signup"><Button className="bg-white text-brand-blue hover:bg-brand-light rounded-lg h-12 px-7 text-base font-semibold">Get started free</Button></Link>
            <Button onClick={() => setDemoOpen(true)} className="bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-lg h-12 px-7 text-base">Book a Demo</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="sm:col-span-2 lg:col-span-1">
            <Logo size="sm" />
            <p className="mt-4 text-sm text-slate-500 max-w-xs">HR made simple for Botswana's small and medium businesses.</p>
          </div>
          {[{ h: "Product", items: ["Features", "Solutions", "Pricing", "Compliance"] }, { h: "Company", items: ["About", "Careers", "Contact", "Blog"] }, { h: "Legal", items: ["Privacy", "Terms", "Data Protection", "Security"] }].map((col) => (
            <div key={col.h}>
              <h4 className="font-semibold text-brand-navy text-sm mb-3">{col.h}</h4>
              <ul className="space-y-2">{col.items.map((i) => (<li key={i}><a href="#features" className="text-sm text-slate-500 hover:text-brand-blue transition-colors">{i}</a></li>))}</ul>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 py-6 text-center text-sm text-slate-400">© {new Date().getFullYear()} HRFlo · People · Records · Compliance · Gaborone, Botswana</div>
      </footer>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
